import React from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Percent, RotateCcw, Truck } from "lucide-react";
import { Container } from "./Primitives";
import { StaggerItem, motion, staggerParent } from "../lib/motion";

/** Four claims, each written down as a guarantee elsewhere on the site.
 *  Shared canonical items used by both the dark HeroTrust (on the hero
 *  band) and the light TrustBar (on shop routes). Editing here changes
 *  both — one wording, one item set. */
export const TRUST_ITEMS = [
  {
    icon: BadgeCheck,
    top: "Every stone lab-certified before listing",
    bottom: "Report verifiable on the laboratory's own domain",
    to: "/about/verification",
  },
  {
    icon: RotateCcw,
    top: "30-day money back",
    bottom: "Refunded to your card, never store credit",
    to: "/legal/refunds",
  },
  {
    icon: Truck,
    top: "72-hour dispatch",
    bottom: "Insured and signed for, worldwide",
    to: "/legal/shipping",
  },
  {
    icon: Percent,
    top: "0% consultant commission",
    bottom: "Advice that sells you nothing",
    to: "/about/consultants",
  },
];

const ITEMS = TRUST_ITEMS;

/**
 * Glass trust row that lives INSIDE the hero video band, anchored to its foot —
 * the icons sit on the film itself so hero and proof read as one piece.
 */
export function HeroTrust({ delay = 0 }) {
  return (
    <div
      data-testid="hero-trust"
      className="relative border-t border-[#f3efe7]/[0.14] bg-[#0b0906]/[0.38] backdrop-blur-md"
    >
      <Container>
        <motion.div
          className="grid grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-[#f3efe7]/10"
          variants={staggerParent(0.08, delay)}
          initial="hidden"
          animate="show"
        >
          {ITEMS.map(({ icon: Icon, top, bottom, to }) => (
            <StaggerItem key={top}>
              <Link
                to={to}
                data-testid={`hero-trust-${top.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group flex h-full items-center gap-3 px-1 py-4 sm:gap-3.5 sm:py-5 lg:justify-center lg:px-4 lg:py-6"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#f0d693]/30 bg-[#f0d693]/[0.08] transition-colors duration-300 group-hover:border-[#f0d693]/70 sm:h-10 sm:w-10">
                  <Icon className="h-4 w-4 text-[#f0d693]" aria-hidden="true" />
                </span>
                <span className="min-w-0 text-left">
                  <span className="ui block text-[12px] font-medium leading-tight text-[#f3efe7] transition-colors duration-300 group-hover:text-[#f0d693] sm:text-[13px]">
                    {top}
                  </span>
                  <span className="ui mt-0.5 block text-[10px] leading-tight text-[#f3efe7]/75 sm:text-[11px]">
                    {bottom}
                  </span>
                </span>
              </Link>
            </StaggerItem>
          ))}
        </motion.div>
      </Container>
    </div>
  );
}
